import LFCEasyNaviIcon from '_assets/images/easy-navi.svg?react';
import {saveAsCaptureImage} from '_components/common/ScreenCapture';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import {useAuthUser, useLogout} from '_contexts/AuthUserProvider';
import {MenuTabContext} from '_contexts/MenuTabProvider';
import {useMySettings} from '_contexts/MySettingsProvider';
import FeedbackDialog, {resolveFeedbackPageName} from '_templates/pages/FeedbackDialog';
import LFCMySettingPage from '_templates/pages/LFCMySettingPage';
import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';

// セッション中の選択タブを保持
let sessionSelectedTab = '';

import {
  Apps,
  CameraAlt,
  Campaign,
  Close,
  Notifications,
  Person,
  Settings
} from '@mui/icons-material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Slide,
  Tab,
  Tabs,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {makeStyles} from 'tss-react/mui';

import AgentChatDialog from '_components-with-data/aichat/AgentChatDialog';
import LFCIconButton from '_components/inputs/LFCIconButton';
import LFCSystemIconButton from '_components/inputs/LFCSystemIconButton';
import LFCMenu from '../components/LFCMenuContents';
import LFCMenuTab from '../components/LFCMenuTab';
import LFCEasyNavi from './LFCEasyNavi';

export const feedbackButtonAttentionAnimation = {
  name: 'feedback-attention',
  duration: '6s',
  timing: 'ease-in-out'
};

/**
 * Style
 */
const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    height: '100vh'
  },
  title: {
    flexGrow: 1
  },
  digTitle: {
    padding: 0,
    margin: 0
  },
  digContent: {
    // backgroundImage: `url(${BgImg})`,
    // backgroundRepeat: "round",
    height: '75vh'
  },
  main_content: {
    width: '100%'
  },
  main_hide_tool: theme.mixins.toolbar as any,
  main_container: {
    marginTop: '0px',
    padding: theme.spacing(2)
  },
  menuImage: {
    fill: theme.palette.primary.main
  }
}));

/**
 * MENUの展開アニメーション
 */
const Transition = React.forwardRef((props: any, ref: any) => {
  return <Slide direction="down" ref={ref} {...props} />;
});

/**
 * TabにユニークIDを振る
 * @param index
 * @returns
 */
const a11yProps = (index: any) => {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`
  };
};

export interface Props {
  title: string;
  children: React.ReactNode;
}

/**
 * LFC 標準サイト
 * @param props
 * @returns
 */
const LFCMainPage = (props: Props) => {
  const {t, i18n} = useTranslation();
  const {classes} = useStyles();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const [openMySetting, setOpenMySetting] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const mySettings = useMySettings();
  const authUser = useAuthUser();
  const theme = useTheme();

  const [easyNaviOpen, setEasyNaviOpen] = useState(false);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  // AI Agent機能は現状未公開のため会社ID=1（linkwiz）のみ利用可能とする
  const aiAgentEnabled = authUser?.companyId === 1;

  const MenuTabContextData = useContext(MenuTabContext);

  // タブの有効性を検証するヘルパー関数
  const getValidTabValue = (currentTab: string, tabData: any[]): string => {
    if (!tabData.length) return '';
    const isValid = currentTab && tabData.some((tab: any) => tab.tab_name === currentTab);
    return isValid ? currentTab : tabData[0]['tab_name'];
  };

  const [menu, setMenu] = useState(
    MenuTabContextData.menuData.filter((item: any) => item.path === location.pathname)[0]
  );
  // セッション中の選択タブから初期値を取得
  const [tabV, setTabV] = useState<string>(sessionSelectedTab);

  // タブが変更されたらセッション変数も更新
  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
    setTabV(newValue);
    sessionSelectedTab = newValue;
  };

  const [myMenuUpdate, setMyMenuUpdate] = useState<boolean>(false);

  const [startRender, setStartRender] = useState(false);

  useEffect(() => {
    if (MenuTabContextData.menuData.length > 0) {
      if (
        location.pathname !== '/' &&
        MenuTabContextData.menuData.filter((item: any) => item['path'] === location.pathname)
          .length === 0
      ) {
        // ページを参照する権限なしの場合
        console.error(`表示権限のないページに移動しようとしたため、Topページに戻しました`);
        history.replace('/');
      } else {
        setStartRender(true);
      }
    }
    setMenu(MenuTabContextData.menuData.filter((item: any) => item.path === location.pathname)[0]);

    // タブの有効性を検証し、必要に応じて初期化
    const validTab = getValidTabValue(tabV, MenuTabContextData.tabData);
    if (validTab && validTab !== tabV) {
      setTabV(validTab);
      sessionSelectedTab = validTab;
    }

    if (myMenuUpdate) {
      setMyMenuUpdate(false);
    }
  }, [MenuTabContextData, tabV]);

  /**
   * MENU選択
   * @param isOpen
   */
  const menuClick = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  /**
   * ログアウト処理
   * @returns
   */
  const signOut = () => {
    logout();
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  /**
   * 個人設定Openイベント
   * @param event
   */
  const openAcMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * 個人設定Closeイベント
   */
  const closeAcMenu = () => {
    setAnchorEl(null);
  };

  const openAnnouncements = () => {
    window.open('https://linkwiz.gitbook.io/lfc/', '_blank', 'noopener,noreferrer');
    history.push('/');
  };

  if (startRender) {
    const feedbackPageName = resolveFeedbackPageName(
      MenuTabContextData.menuData,
      location.pathname,
      props.title,
      t
    );

    return (
      <Box className={classes.root}>
        {/*    <AppBar position="fixed" style={{backgroundColor: theme.palette.background.default}}>*/}
        <AppBar position="fixed">
          <Toolbar>
            <Tooltip title={t('メニュー')}>
              <LFCSystemIconButton
                edge="start"
                onClick={() => menuClick(true)}
                color="default"
                sx={{mr: 1}}
              >
                <Apps />
              </LFCSystemIconButton>
            </Tooltip>
            <Typography component="div" variant="h6" noWrap className={classes.title}>
              {props.title}
            </Typography>

            <Box mr={2}>
              {import.meta.env.VITE_ENV !== 'production' ? (
                <Typography
                  component="div"
                  variant="h6"
                  noWrap
                  className={classes.title}
                  color={theme.palette.background.default}
                  sx={{backgroundColor: theme.palette.secondary.main}}
                >
                  {`　${import.meta.env.VITE_ENV}-${mySettings.region}　`}
                </Typography>
              ) : (
                <></>
              )}
            </Box>

            <Box sx={{display: 'flex'}}>
              <Tooltip title={t('フィードバック')}>
                <LFCSystemIconButton
                  onClick={() => setOpenFeedback(true)}
                  sx={{
                    animation: `${feedbackButtonAttentionAnimation.name} ${feedbackButtonAttentionAnimation.duration} ${feedbackButtonAttentionAnimation.timing} infinite`,
                    '& svg': {
                      animation: `feedback-icon-attention ${feedbackButtonAttentionAnimation.duration} ${feedbackButtonAttentionAnimation.timing} infinite`,
                      transformOrigin: '50% 50%'
                    },
                    '@keyframes feedback-attention': {
                      '0%, 78%, 100%': {
                        transform: 'translateX(0)'
                      },
                      '82%': {
                        transform: 'translateX(-1px)'
                      },
                      '84%': {
                        transform: 'translateX(2px)'
                      },
                      '86%': {
                        transform: 'translateX(-2px)'
                      },
                      '88%': {
                        transform: 'translateX(2px)'
                      },
                      '92%': {
                        transform: 'translateX(0)'
                      }
                    },
                    '@keyframes feedback-icon-attention': {
                      '0%, 78%, 100%': {
                        color: theme.palette.text.primary,
                        filter: 'drop-shadow(0 0 0 transparent)',
                        transform: 'translateX(0) scale(1)'
                      },
                      '82%': {
                        color: theme.palette.secondary.light,
                        filter: `drop-shadow(0 0 4px ${theme.palette.secondary.light})`,
                        transform: 'translateX(-1px) scale(1.14)'
                      },
                      '84%': {
                        color: theme.palette.secondary.light,
                        filter: `drop-shadow(0 0 8px ${theme.palette.secondary.light})`,
                        transform: 'translateX(2px) scale(1.14)'
                      },
                      '86%': {
                        color: theme.palette.secondary.light,
                        filter: `drop-shadow(0 0 8px ${theme.palette.secondary.light})`,
                        transform: 'translateX(-2px) scale(1.14)'
                      },
                      '88%': {
                        color: theme.palette.secondary.light,
                        filter: `drop-shadow(0 0 8px ${theme.palette.secondary.light})`,
                        transform: 'translateX(2px) scale(1.14)'
                      },
                      '92%': {
                        color: theme.palette.secondary.light,
                        filter: `drop-shadow(0 0 4px ${theme.palette.secondary.light})`,
                        transform: 'translateX(0) scale(1.08)'
                      }
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      '& svg': {
                        animation: 'none'
                      }
                    }
                  }}
                >
                  <Campaign />
                </LFCSystemIconButton>
              </Tooltip>
              {aiAgentEnabled ? (
                <Tooltip title={t('AI Agent')}>
                  <LFCSystemIconButton onClick={() => setAgentDialogOpen(true)}>
                    <SmartToyIcon />
                  </LFCSystemIconButton>
                </Tooltip>
              ) : (
                <></>
              )}
              {mySettings.locale === 'ja' ? (
                <Tooltip title={t('簡単ナビ')}>
                  <LFCSystemIconButton onClick={() => setEasyNaviOpen(true)}>
                    <LFCEasyNaviIcon fill={theme.palette.text.primary} width="1em" height="1em" />
                  </LFCSystemIconButton>
                </Tooltip>
              ) : (
                <></>
              )}
              <Tooltip title={t('お知らせ')}>
                <LFCSystemIconButton onClick={openAnnouncements}>
                  <Notifications />
                </LFCSystemIconButton>
              </Tooltip>
              <Tooltip title={t('キャプチャ保存')}>
                <LFCSystemIconButton onClick={() => saveAsCaptureImage('bodyRoot', props.title)}>
                  <CameraAlt />
                </LFCSystemIconButton>
              </Tooltip>
              <Tooltip title={t('アカウント')}>
                <LFCSystemIconButton onClick={openAcMenu} style={{margin: 0}}>
                  <Person />
                </LFCSystemIconButton>
              </Tooltip>
            </Box>

            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeAcMenu}
              variant="menu"
            >
              <MenuItem>
                <Card style={{width: '100%'}}>
                  <CardContent>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Person />
                    </Box>
                    <Box display="flex" justifyContent="center">
                      <Typography variant="subtitle2">{mySettings.companyName}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="center">
                      <Typography variant="subtitle1">{mySettings.userName}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeAcMenu();
                  setOpenMySetting(true);
                }}
              >
                <LFCIconButton onClick={() => {}}>
                  <Settings />
                </LFCIconButton>
                {t('個人設定')}
              </MenuItem>
              <MenuItem onClick={signOut}>
                <LFCIconButton onClick={() => {}}>
                  <Close />
                </LFCIconButton>
                {t('ログアウト')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <main id="mainContent" className={classes.main_content}>
          <Box className={classes.main_hide_tool} />
          <Box className={classes.main_container}>{props.children}</Box>
        </main>
        {/* AI調査ダイアログ: AI未公開の会社ではkeepMountedのパネルが履歴APIを叩いてしまうため、ダイアログ自体をマウントしない */}
        {aiAgentEnabled && (
          <AgentChatDialog open={agentDialogOpen} onClose={() => setAgentDialogOpen(false)} />
        )}
        {/* フィードバック投稿ダイアログ */}
        <FeedbackDialog
          open={openFeedback}
          onClose={() => setOpenFeedback(false)}
          companyId={authUser?.companyId ?? 0}
          companyName={mySettings.companyName ?? ''}
          userName={mySettings.userName ?? authUser?.userId ?? ''}
          email={authUser?.email ?? ''}
          pageName={feedbackPageName}
          pathname={location.pathname}
        />
        {/* メニューダイアログ */}
        <Dialog
          fullWidth
          maxWidth={'xl'}
          open={open}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => menuClick(false)}
        >
          <DialogTitle className={classes.digTitle}>
            <AppBar position="static">
              {MenuTabContextData.tabData.length > 0 && (
                <Tabs
                  value={getValidTabValue(tabV, MenuTabContextData.tabData)}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {MenuTabContextData.tabData.map((rmt: any, index: number) => {
                    return (
                      <Tab
                        value={rmt['tab_name']}
                        label={t(`menu.tab_name.${rmt['tab_name']}`)}
                        wrapped
                        {...a11yProps(rmt['tab_name'])}
                        key={index}
                      />
                    );
                  })}
                </Tabs>
              )}
            </AppBar>
          </DialogTitle>
          <DialogContent dividers className={classes.digContent}>
            {MenuTabContextData.tabData.map((rmt: any, i: number) => {
              return (
                <LFCMenuTab
                  value={getValidTabValue(tabV, MenuTabContextData.tabData)}
                  index={rmt['tab_name']}
                  key={i}
                >
                  <Grid container spacing={1}>
                    {MenuTabContextData.menuData
                      .filter((v: any) => rmt['tab_name'] === v['tab_name'])
                      .map((rmd: any, j: number) => {
                        return (
                          <LFCMenu path={rmd.path} name={rmd.name} exText={rmd.ex_text} key={j} />
                        );
                      })}
                  </Grid>
                </LFCMenuTab>
              );
            })}
          </DialogContent>
          <DialogActions>
            <LFCButton onClick={() => menuClick(false)}>Close</LFCButton>
          </DialogActions>
        </Dialog>
        {/* 個人設定ダイアログ */}
        <Dialog
          open={openMySetting}
          onClose={() => setOpenMySetting(false)}
          maxWidth={'lg'}
          TransitionComponent={Transition}
        >
          <LFCDialogTitle onClose={() => setOpenMySetting(false)}>{t('個人設定')}</LFCDialogTitle>
          <DialogContent dividers>
            <LFCMySettingPage />
          </DialogContent>
        </Dialog>
        {/* 簡単ナビダイアログ */}
        <Dialog
          open={easyNaviOpen}
          onClose={() => setEasyNaviOpen(false)}
          fullWidth
          maxWidth={'xl'}
          TransitionComponent={Transition}
          keepMounted
        >
          <DialogContent dividers>
            <LFCEasyNavi onClose={() => setEasyNaviOpen(false)} />
          </DialogContent>
          <DialogActions>
            <LFCButton onClick={() => setEasyNaviOpen(false)}>{t('閉じる')}</LFCButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  } else {
    return <></>;
  }
};

export default LFCMainPage;
